/**
 * C.13c — FlowEditor: XYFlow surface for `.flow` files.
 *
 * Renders a node palette + a canvas. Drag-to-position fires
 * `onChange` with the re-serialized JSON via `flowSerializer`. The
 * saveProvider in the parent treats the result as bytes (no special
 * handling vs markdown).
 *
 * Custom node types: Case / Statute / Party / Evidence / Note / Text.
 * Single edge type: RelationEdge.
 *
 * The in-place mount lives inside ObsidianVaultWindow; the popout
 * (VaultFlowWindow) shares this component as the body.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  Controls,
  ReactFlow,
  applyEdgeChanges,
  applyNodeChanges,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  deserializeFlow,
  serializeFlow,
  type FlowDoc,
  type FlowEdge,
  type FlowNode,
  type FlowNodeKind,
} from './flowSerializer';
import { CaseNode } from './nodes/CaseNode';
import { StatuteNode } from './nodes/StatuteNode';
import { PartyNode } from './nodes/PartyNode';
import { EvidenceNode } from './nodes/EvidenceNode';
import { NoteNode } from './nodes/NoteNode';
import { TextNode } from './nodes/TextNode';
import { RelationEdge } from './edges/RelationEdge';
import { testIds } from '../../../lib/testIds';
import './FlowEditor.css';

// Module-scope: XYFlow whines about new identity per render.
const NODE_TYPES = {
  CaseNode,
  StatuteNode,
  PartyNode,
  EvidenceNode,
  NoteNode,
  TextNode,
};
const EDGE_TYPES = { RelationEdge };
const PRO_OPTIONS = { hideAttribution: true };

const PALETTE_ITEMS: { kind: FlowNodeKind; label: string; defaults: Record<string, unknown> }[] = [
  { kind: 'CaseNode', label: '⚖️  Case', defaults: { title: 'New case' } },
  { kind: 'StatuteNode', label: '📜  Statute', defaults: { title: 'New statute' } },
  { kind: 'PartyNode', label: '👤  Party', defaults: { title: 'New party' } },
  { kind: 'EvidenceNode', label: '🔍  Evidence', defaults: { title: 'New evidence' } },
  { kind: 'NoteNode', label: '📝  Note', defaults: { title: 'New note' } },
  { kind: 'TextNode', label: '✏️  Text', defaults: { body: 'Text…' } },
];

export interface FlowEditorProps {
  /** The .flow file path (for keying re-mounts when user switches files). */
  path: string;
  /** Initial JSON bytes from disk. */
  initialRaw: string;
  /** Fires whenever the canvas mutates; payload is the re-serialized JSON. */
  onChange?: (nextRaw: string) => void;
  /** Optional pop-out request (in-place mount renders the button). */
  onPopout?: () => void;
}

export function FlowEditor({ path, initialRaw, onChange, onPopout }: FlowEditorProps) {
  const initial = useMemo(() => deserializeFlow(initialRaw, deriveName(path)), [initialRaw, path]);
  const [doc, setDoc] = useState<FlowDoc>(initial);
  // Reset when the parent passes a different file in.
  useEffect(() => {
    setDoc(initial);
  }, [initial]);

  const lastEmittedRef = useRef<string>(initialRaw);
  const onChangeRef = useRef<((next: string) => void) | undefined>(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Emit a save whenever the doc changes.
  useEffect(() => {
    const next = serializeFlow(doc);
    if (next !== lastEmittedRef.current) {
      lastEmittedRef.current = next;
      onChangeRef.current?.(next);
    }
  }, [doc]);

  const reactFlowNodes: Node[] = useMemo(
    () =>
      doc.nodes.map((n) => ({
        id: n.id,
        type: n.type,
        position: n.position,
        data: n.data,
      })),
    [doc.nodes],
  );
  const reactFlowEdges: Edge[] = useMemo(
    () =>
      doc.edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        type: e.type ?? 'RelationEdge',
        label: e.label,
        data: e.data,
      })),
    [doc.edges],
  );

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setDoc((prev) => {
      const next = applyNodeChanges(changes, prev.nodes.map(asXyNode));
      return {
        ...prev,
        nodes: next.map(toFlowNode),
      };
    });
  }, []);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setDoc((prev) => {
      const next = applyEdgeChanges(changes, prev.edges.map(asXyEdge));
      return {
        ...prev,
        edges: next.map(toFlowEdge),
      };
    });
  }, []);

  function addNodeOfKind(kind: FlowNodeKind, defaults: Record<string, unknown>) {
    setDoc((prev) => ({
      ...prev,
      nodes: [
        ...prev.nodes,
        {
          id: `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          type: kind,
          position: { x: 80 + prev.nodes.length * 20, y: 80 + prev.nodes.length * 20 },
          data: { ...defaults },
        },
      ],
    }));
  }

  return (
    <div data-testid={testIds.flow.root}>
      <div className="flow-palette" data-testid={testIds.flow.nodePalette}>
        <div style={{ fontSize: 10, color: '#444', marginBottom: 4, fontWeight: 'bold' }}>
          ADD NODE
        </div>
        {PALETTE_ITEMS.map((p) => (
          <button
            key={p.kind}
            type="button"
            className="flow-palette-item"
            data-testid={testIds.flow.paletteItem(p.kind)}
            onClick={() => addNodeOfKind(p.kind, p.defaults)}
          >
            {p.label}
          </button>
        ))}
        {onPopout && (
          <button
            type="button"
            data-testid={testIds.flow.popoutButton}
            onClick={onPopout}
            style={{ marginTop: 'auto', padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}
          >
            ↗  Pop out
          </button>
        )}
        {doc.error && (
          <div
            style={{
              marginTop: 6,
              color: '#a00',
              fontSize: 10,
              background: '#fff0f0',
              padding: 4,
              border: '1px solid #d99',
            }}
          >
            Parse error: {doc.error}
          </div>
        )}
      </div>
      <div className="flow-canvas-wrap">
        <ReactFlow
          data-testid={testIds.flow.canvas}
          nodes={reactFlowNodes}
          edges={reactFlowEdges}
          nodeTypes={NODE_TYPES}
          edgeTypes={EDGE_TYPES}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          fitView
          proOptions={PRO_OPTIONS}
          minZoom={0.2}
          maxZoom={3}
          style={{ background: '#fafafa' }}
        >
          <Background gap={20} color="#e0e0e0" />
          <Controls position="bottom-right" />
        </ReactFlow>
      </div>
    </div>
  );
}

function deriveName(path: string): string {
  const slash = path.lastIndexOf('/');
  const basename = slash >= 0 ? path.slice(slash + 1) : path;
  const dot = basename.lastIndexOf('.');
  return dot > 0 ? basename.slice(0, dot) : basename;
}

function asXyNode(n: FlowNode): Node {
  return {
    id: n.id,
    type: n.type,
    position: n.position,
    data: n.data,
  };
}

function asXyEdge(e: FlowEdge): Edge {
  return {
    id: e.id,
    source: e.source,
    target: e.target,
    type: e.type ?? 'RelationEdge',
    label: e.label,
    data: e.data,
  };
}

function toFlowNode(n: Node): FlowNode {
  return {
    id: n.id,
    type: (n.type ?? 'NoteNode') as FlowNodeKind,
    position: n.position,
    data: (n.data as Record<string, unknown>) ?? {},
  };
}

function toFlowEdge(e: Edge): FlowEdge {
  return {
    id: e.id,
    source: e.source,
    target: e.target,
    type: e.type,
    label: typeof e.label === 'string' ? e.label : undefined,
    data: (e.data as Record<string, unknown>) ?? undefined,
  };
}
